import { Injectable } from '@angular/core';
import { File, FileEntry } from '@awesome-cordova-plugins/file/ngx';
import { Nullable } from 'src/core/common/domain/types/types';

@Injectable({
  providedIn: 'root',
})
export class FilesystemService {
  constructor(private file: File) {}

  async copyFileToFolder(inputFilePath: string, folder: string): Promise<FileEntry> {
    const sourceDirectory = this.getFolder(inputFilePath);
    const sourceFilename = this.getFilename(inputFilePath);
    const destinationFilename = sourceFilename;
    const destinationDirectory = this.file.dataDirectory + folder;
    const created = await this.ensureDirectoryIsCreated(this.file.dataDirectory, folder);
    if (!created) {
      throw new Error('Destination directory cannot be created');
    }

    const entry = await this.file.copyFile(sourceDirectory, sourceFilename, destinationDirectory, destinationFilename);
    return entry as FileEntry;
  }

  async writeBlobToFolder(folder: string, filename: string, blob: Blob): Promise<FileEntry> {
    const destinationDirectory = this.file.dataDirectory + folder;
    const created = await this.ensureDirectoryIsCreated(this.file.dataDirectory, folder);

    if (!created) {
      throw new Error('Destination directory cannot be created');
    }

    try {
      await this.file.checkFile(destinationDirectory, filename);
      await this.file.removeFile(destinationDirectory, filename);
    } catch (_error) {}

    const entry = await this.file.writeFile(destinationDirectory, filename, blob, { replace: true });

    return entry as FileEntry;
  }

  async fileExists(folder: string, filename: string): Promise<boolean> {
    const directory = this.file.dataDirectory + folder + '/';

    try {
      await this.file.checkFile(directory, filename);
      return true;
    } catch (_error) {
      return false;
    }
  }

  async resolveExistingFilePath(folder: string, filename: string): Promise<Nullable<string>> {
    const exists = await this.fileExists(folder, filename);
    if (!exists) {
      return null;
    }

    return this.file.dataDirectory + folder + '/' + filename;
  }

  async buildUniqueFilename(folder: string, filename: string): Promise<string> {
    const extension = this.getExtension(filename);
    const baseName = this.getFilenameWithoutExtension(filename);

    let candidate = filename;
    let counter = 1;

    while (await this.fileExists(folder, candidate)) {
      candidate = extension ? `${baseName} (${counter}).${extension}` : `${baseName} (${counter})`;

      counter++;
    }

    return candidate;
  }

  async ensureDirectoryIsCreated(baseDirectory: string, directory: string): Promise<boolean> {
    try {
      await this.file.createDir(baseDirectory, directory, false);
      return true;
    } catch {
      try {
        await this.file.checkDir(baseDirectory, directory);
        return true;
      } catch {
        return false;
      }
    }
  }

  async readAsDataURL(filePath: string): Promise<string> {
    const folder = this.getFolder(filePath);
    const filename = this.getFilename(filePath);

    return await this.file.readAsDataURL(folder, filename);
  }

  async readAsBinaryURL(filePath: string): Promise<string> {
    const folder = this.getFolder(filePath);
    const filename = this.getFilename(filePath);

    return await this.file.readAsBinaryString(folder, filename);
  }

  async deleteDirectory(directory: string): Promise<void> {
    try {
      const baseDirectory = this.file.dataDirectory;
      await this.file.checkDir(baseDirectory, directory);
      await this.file.removeDir(baseDirectory, directory);
    } catch (error) {}
  }

  async deleteFile(folder: string, filename: string): Promise<void> {
    const directory = this.file.dataDirectory + folder + '/';

    try {
      await this.file.checkFile(directory, filename);
      await this.file.removeFile(directory, filename);
    } catch (_error) {}
  }

  getFolder(filePath: string): string {
    return filePath.substring(0, filePath.lastIndexOf('/') + 1);
  }

  getFilename(filePath: string): string {
    return filePath.substring(filePath.lastIndexOf('/') + 1).replace(/\?.*/, '');
  }

  getFilenameFromUrl(url: string): string {
    const cleanUrl = url.split('?')[0];
    const lastSegment = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1);

    return lastSegment || 'download';
  }

  getFilenameWithoutExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');

    if (lastDotIndex <= 0) {
      return filename;
    }

    return filename.substring(0, lastDotIndex);
  }

  getExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');

    if (lastDotIndex <= 0 || lastDotIndex === filename.length - 1) {
      return '';
    }

    return filename.substring(lastDotIndex + 1);
  }

  sanitizeFilename(filename: string): string {
    return (
      filename
        // eslint-disable-next-line no-control-regex -- el rango de control es justo lo que se quiere quitar
        .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
        .replace(/\s+/g, ' ')
        .trim()
    );
  }

  async getFileSize(url: string): Promise<number> {
    const binaryContent = await this.readAsBinaryURL(url);

    return binaryContent.length;
  }

  async printBase64Info(url: string) {
    const base64Content = await this.readAsDataURL(url);
    const length = base64Content.length;
    console.log(
      `base64Content (${length}): ${base64Content.substring(0, 40)} .. ${base64Content.substring(length - 20)}`
    );
  }
}
